import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IntegrationsService } from '@core/services/integrations/integrations.service';
import {
  INTEGRATION_FIND,
  INTEGRATION_FIND_SUCCESS,
  INTEGRATION_FIND_ALL,
  INTEGRATION_FIND_ALL_SUCCESS,
  INTEGRATION_CREATE,
  INTEGRATION_CREATE_COMPLETED,
  INTEGRATION_UPDATE,
  INTEGRATION_UPDATE_COMPLETED,
  INTEGRATION_REMOVE,
  INTEGRATION_REMOVE_COMPLETED,
  INTEGRATION_FAILURE,
  INTEGRATION_UPDATE_LOADING,
} from '@store/actions/integration.action';
import {
  selectIntegrationForm,
  selectIntegrationId,
} from '@store/selectors/integration.selectors';
import {
  catchError,
  map,
  switchMap,
  withLatestFrom,
  finalize,
  mergeMap,
  filter,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import {
  OnboardingOrderOutIntegration,
  OnboardingOrderOutIntegrationUpsert,
} from '@core/models/integrations/orderOut';
import {
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
} from '@store/actions/notification.action';

@Injectable()
export class IntegrationEffects {
  constructor(
    private actions$: Actions,
    private integrationsService: IntegrationsService,
    private store: Store,
  ) {}

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INTEGRATION_FIND_ALL),
      switchMap(({ storeId }) => {
        this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: true }));
        return this.integrationsService.findAll(storeId).pipe(
          map((response) =>
            INTEGRATION_FIND_ALL_SUCCESS({ integrationList: response.data }),
          ),
          catchError((error) =>
            of(INTEGRATION_FAILURE({ error: error.error })),
          ),
          finalize(() =>
            this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: false })),
          ),
        );
      }),
    ),
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INTEGRATION_FIND),
      switchMap(({ id }) => {
        this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: true }));
        return this.integrationsService.find(id).pipe(
          mergeMap(({ data }) => {
            const [integration] = data as OnboardingOrderOutIntegration[];
            return [
              INTEGRATION_FIND_SUCCESS({ integration }),
              INTEGRATION_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              INTEGRATION_FAILURE({ error: error.error }),
              INTEGRATION_UPDATE_LOADING({ loading: false }),
            ),
          ),
        );
      }),
    ),
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INTEGRATION_CREATE),
      withLatestFrom(this.store.select(selectIntegrationForm)),
      filter(([_, integrationForm]) => !!integrationForm),
      map(([_, integrationForm]) => ({
        integrationForm: integrationForm as OnboardingOrderOutIntegrationUpsert,
      })),
      mergeMap(({ integrationForm }) => {
        this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: true }));
        return this.integrationsService.create(integrationForm).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Integration created successfully',
              },
            }),
            INTEGRATION_CREATE_COMPLETED(),
            INTEGRATION_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              INTEGRATION_FAILURE({ error }),
              INTEGRATION_UPDATE_LOADING({ loading: false }),
            ),
          ),
        );
      }),
    ),
  );

  createComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INTEGRATION_CREATE_COMPLETED),
        tap(() => {
          this.integrationsService.createCompleted();
        }),
      ),
    { dispatch: false },
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INTEGRATION_UPDATE),
      withLatestFrom(
        this.store.select(selectIntegrationForm),
        this.store.select(selectIntegrationId),
      ),
      filter(([_, apkForm, apkId]) => !!apkForm && !!apkId),
      map(([_, integrationForm, apkId]) => ({
        apkForm: integrationForm as OnboardingOrderOutIntegrationUpsert,
        apkId,
      })),
      mergeMap(({ apkForm, apkId }) => {
        this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: true }));
        return this.integrationsService.update(apkId!, apkForm).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Integration updated successfully',
              },
            }),
            INTEGRATION_UPDATE_COMPLETED(),
            INTEGRATION_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              INTEGRATION_FAILURE({ error }),
              INTEGRATION_UPDATE_LOADING({ loading: false }),
            ),
          ),
        );
      }),
    ),
  );

  updateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INTEGRATION_UPDATE_COMPLETED),
        tap(() => {
          this.integrationsService.updateCompleted();
        }),
      ),
    { dispatch: false },
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INTEGRATION_REMOVE),
      mergeMap(({ id }) => {
        this.store.dispatch(INTEGRATION_UPDATE_LOADING({ loading: true }));
        return this.integrationsService.remove(id).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Integration removed successfully',
              },
            }),
            INTEGRATION_REMOVE_COMPLETED(),
            INTEGRATION_UPDATE_LOADING({ loading: false }),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              INTEGRATION_FAILURE({ error }),
              INTEGRATION_UPDATE_LOADING({ loading: false }),
            ),
          ),
        );
      }),
    ),
  );

  removeComplete$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(INTEGRATION_REMOVE_COMPLETED),
          tap(() => {})
        ),
      { dispatch: false }
    );
}
